import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck } from 'lucide-react';
import { PERMISSIONS, SupplierActiveFilter, type SupplierSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { BooleanStatusBadge } from '@/components/ui/StatusBadge';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useSuppliersQuery } from '../api/suppliers.api';
import { SupplierFormDialog } from '../components/SupplierFormDialog';

const PAGE_SIZE = 20;

/**
 * Who the tenant buys from.
 *
 * Tenant-wide master data, so no branch is needed to read or manage it. Searching and filtering
 * happen on the server: a supplier list can outgrow a page long before anyone notices, and
 * filtering a partial page in the browser would quietly hide the rest.
 */
export function SuppliersPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [active, setActive] = useState<SupplierActiveFilter>(SupplierActiveFilter.ACTIVE);
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useSuppliersQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    active,
  });

  const canManage = can(PERMISSIONS.SUPPLIERS_MANAGE);

  const activeOptions = [
    { value: SupplierActiveFilter.ACTIVE, label: t('purchasing.suppliers.filters.active') },
    { value: SupplierActiveFilter.INACTIVE, label: t('purchasing.suppliers.filters.inactive') },
    { value: SupplierActiveFilter.ALL, label: t('purchasing.suppliers.filters.all') },
  ];

  const columns: Column<SupplierSummary>[] = [
    {
      key: 'supplier',
      header: t('purchasing.suppliers.fields.name'),
      primary: true,
      render: (supplier) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium" dir="auto">
            {supplier.name}
          </span>
          {supplier.nameAr ? (
            <span className="text-muted text-xs" dir="auto">
              {supplier.nameAr}
            </span>
          ) : null}
          <span className="text-muted font-mono text-xs" dir="ltr">
            {supplier.supplierNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'phone',
      header: t('purchasing.suppliers.fields.phone'),
      render: (supplier) =>
        supplier.phone ? (
          <span className="text-ink" dir="ltr">
            {supplier.phone}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'terms',
      header: t('purchasing.suppliers.fields.paymentTermsDays'),
      align: 'end',
      render: (supplier) => (
        <span className="text-muted" dir="ltr">
          {t('purchasing.suppliers.days', { count: supplier.paymentTermsDays })}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('inventory.fields.state'),
      render: (supplier) => <BooleanStatusBadge isActive={supplier.isActive} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (supplier) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/suppliers/${supplier.id}`)}
          aria-label={t('purchasing.suppliers.openNamed', { name: supplier.name })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.suppliers.title')}
        subtitle={t('purchasing.suppliers.subtitle')}
        actions={
          canManage ? (
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t('purchasing.suppliers.create')}
            </Button>
          ) : undefined
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      <Card>
        <CardHeader
          title={t('purchasing.suppliers.title')}
          subtitle={
            data
              ? t('purchasing.suppliers.count', { count: data.pagination.totalItems })
              : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-56">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('purchasing.suppliers.searchPlaceholder')}
                  aria-label={t('purchasing.suppliers.searchLabel')}
                />
              </div>
              <div className="w-36">
                <Select
                  options={activeOptions}
                  value={active}
                  aria-label={t('purchasing.suppliers.filters.label')}
                  onChange={(event) => {
                    setActive(event.target.value as SupplierActiveFilter);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('purchasing.suppliers.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(supplier) => supplier.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('purchasing.suppliers.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Truck}
          emptyTitle={
            deferredSearch ? t('purchasing.suppliers.noMatches') : t('purchasing.suppliers.empty')
          }
          emptyDescription={
            deferredSearch
              ? t('purchasing.suppliers.noMatchesHint')
              : t('purchasing.suppliers.emptyHint')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      <SupplierFormDialog
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onSaved={(supplier) => {
          setIsCreating(false);
          setFeedback(t('purchasing.suppliers.created', { number: supplier.supplierNumber }));
        }}
      />
    </div>
  );
}
